import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight, BarChart3 } from 'lucide-react'
import { api } from '../../lib/api'
import { Table, type Colonna } from '../../components/ui/Table'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { EmptyState } from '../../components/ui/EmptyState'
import { formatDataOra, formatDurata } from '../../lib/format'
import pagina from '../pagine.module.css'

interface LeadInfo {
  id: string
  nome: string
  ruolo: string | null
  settore: string | null
}

interface SessTeam {
  id: string
  userId: string
  durataSecondi: number
  votoComplessivo: number | null
  metodoAvvio: 'preselezionato' | 'ivr' | 'browser' | null
  completata: boolean
  /** Snapshot lead dal backend (null se eliminato o non associato) */
  lead: LeadInfo | null
  /** true/false/null: null = valutazione non disponibile per sessioni vecchie */
  obiettivoRaggiunto: boolean | null
  createdAt: string
}

interface Risposta {
  sessioni: SessTeam[]
  totale: number
  page: number
  per_pagina: number
}

interface MembroTeam {
  membro_id: string
  utente: { id: string; nome: string; cognome: string }
}

// GET /manager/team ritorna { membri, inviti_in_attesa }: qui ci servono
// solo i membri per popolare il filtro dropdown.
interface RispostaTeamLista {
  membri: MembroTeam[]
}

export function ManagerSessioni() {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [userIdFiltro, setUserIdFiltro] = useState<string>('')
  const [minScore, setMinScore] = useState<number | ''>('')

  const team = useQuery({
    queryKey: ['manager', 'team', 'lista-base'],
    queryFn: async () =>
      (await api.get<{ dati: RispostaTeamLista }>('/manager/team')).data.dati,
  })

  // Lista membri per filtro/mapping: gestione esplicita del caso "team
  // ancora vuoto" o "errore di rete" così l'iterazione non esplode.
  const membri = team.data?.membri ?? []

  const q = useQuery({
    queryKey: ['manager', 'sessioni', page, userIdFiltro, minScore],
    queryFn: async () => {
      const params: Record<string, unknown> = { page, per_pagina: 20 }
      if (userIdFiltro) params.user_id = userIdFiltro
      if (minScore !== '') params.min_score = minScore
      return (await api.get<{ dati: Risposta }>('/manager/sessioni', { params })).data.dati
    },
  })

  if (q.isLoading) return <div className={pagina.skeleton}>Caricamento…</div>
  if (q.error || !q.data) return <EmptyState titolo="Errore caricamento" />

  // Mappa userId → nome per visualizzare il membro nella riga
  const nomePerId = new Map<string, string>()
  for (const m of membri) {
    nomePerId.set(m.utente.id, `${m.utente.nome} ${m.utente.cognome}`)
  }

  const colonne: Colonna<SessTeam>[] = [
    { chiave: 'data', intestazione: 'Data', render: (s) => formatDataOra(s.createdAt), larghezza: '170px' },
    {
      chiave: 'membro', intestazione: 'Membro',
      render: (s) => nomePerId.get(s.userId) ?? <span style={{ color: 'var(--text-muted)' }}>—</span>,
    },
    {
      chiave: 'lead', intestazione: 'Lead',
      render: (s) => <CellaLead lead={s.lead} />,
      larghezza: '180px',
    },
    { chiave: 'durata', intestazione: 'Durata', render: (s) => <span className="mono">{formatDurata(s.durataSecondi)}</span>, larghezza: '100px' },
    {
      chiave: 'voto', intestazione: 'Voto', allineamento: 'right',
      render: (s) => s.votoComplessivo != null
        ? <span className="mono" style={{ fontWeight: 600 }}>{s.votoComplessivo}/10</span>
        : <span style={{ color: 'var(--text-muted)' }}>—</span>,
      larghezza: '90px',
    },
    {
      chiave: 'obiettivo', intestazione: 'Obiettivo',
      render: (s) => <BadgeObiettivo raggiunto={s.obiettivoRaggiunto} />,
      larghezza: '150px',
    },
    {
      chiave: 'stato', intestazione: 'Stato',
      render: (s) => s.completata
        ? <Badge variante="success">Completata</Badge>
        : <Badge variante="warning">Incompleta</Badge>,
      larghezza: '130px',
    },
  ]

  const totalePagine = Math.max(1, Math.ceil(q.data.totale / q.data.per_pagina))

  return (
    <div className={pagina.wrap}>
      <div className={pagina.header}>
        <div>
          <h1 className={pagina.titolo}>Sessioni del team</h1>
          <div className={pagina.sottotitolo}>{q.data.totale} sessioni totali</div>
        </div>
      </div>

      <div className={pagina.filtri}>
        <select
          className={pagina.select}
          value={userIdFiltro}
          onChange={(e) => { setUserIdFiltro(e.target.value); setPage(1) }}
        >
          <option value="">Tutti i membri</option>
          {membri.map((m) => (
            <option key={m.utente.id} value={m.utente.id}>
              {m.utente.nome} {m.utente.cognome}
            </option>
          ))}
        </select>
        <select
          className={pagina.select}
          value={minScore}
          onChange={(e) => { setMinScore(e.target.value ? parseInt(e.target.value, 10) : ''); setPage(1) }}
        >
          <option value="">Tutti i voti</option>
          <option value="7">≥ 7</option>
          <option value="5">≥ 5</option>
          <option value="3">≥ 3</option>
        </select>
      </div>

      <Table
        colonne={colonne}
        righe={q.data.sessioni}
        onClickRiga={(s) => navigate(`/app/sessioni/${s.id}`)}
        vuoto={<EmptyState icona={<BarChart3 size={40} />} titolo="Nessuna sessione trovata" />}
      />

      {totalePagine > 1 && (
        <div className={pagina.paginazione}>
          <span>Pagina {q.data.page} di {totalePagine}</span>
          <div className={pagina.paginazioneBottoni}>
            <Button taglia="sm" variante="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)} iconaSinistra={<ChevronLeft size={14} />}>
              Indietro
            </Button>
            <Button taglia="sm" variante="secondary" disabled={page >= totalePagine} onClick={() => setPage(page + 1)} iconaDestra={<ChevronRight size={14} />}>
              Avanti
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Nome del lead usato nella sessione. "Lead non disponibile" quando
 * il record `lead_profiles` è stato eliminato o la sessione non ha
 * lead associato.
 */
function CellaLead({ lead }: { lead: LeadInfo | null }) {
  if (!lead) {
    return <span style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>Lead non disponibile</span>
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <span style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {lead.nome}
      </span>
      {(lead.ruolo || lead.settore) && (
        <span style={{ fontSize: 11, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {[lead.ruolo, lead.settore].filter(Boolean).join(' · ')}
        </span>
      )}
    </div>
  )
}

/** Badge esito obiettivo: true→verde, false→rosso, null→neutro N/D. */
function BadgeObiettivo({ raggiunto }: { raggiunto: boolean | null }) {
  if (raggiunto === true) return <Badge variante="success">✓ Raggiunto</Badge>
  if (raggiunto === false) return <Badge variante="danger">✘ Non raggiunto</Badge>
  return <Badge variante="default">N/D</Badge>
}
